import { debugLogger, LogLevel } from './debugLogger';
import { realtimeCentral } from '@/core/RealtimeCentral';

/**
 * Enhanced Debug utilities for development - now with window.DebugUtils interface
 */

// CORREÇÃO: Usar a mesma lógica de detecção de ambiente do main.tsx
const getEnvironment = () => {
  const hostname = window.location.hostname;
  
  // Production environments (highest priority)
  if (hostname === 'interlaced.lovable.app' || hostname === 'interlaced.app') {
    return 'production';
  }
  
  // Preview environments (staging URLs)
  if (hostname === 'preview--interlaced.lovable.app') {
    return 'preview';
  }
  
  // Preview environments with ID prefix
  if (hostname.includes('id-preview--') && hostname.includes('.lovable.app')) {
    return 'preview';
  }
  
  // Preview environments with ID and commit prefix
  if (hostname.includes('id-preview-') && hostname.includes('--') && hostname.includes('.lovable.app')) {
    return 'preview';
  }
  
  // Development environment (Lovable editor interface)
  if (hostname.includes('.lovableproject.com')) {
    return 'development';
  }
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  
  // Any other .lovable.app domains (fallback for preview)
  if (hostname.includes('.lovable.app')) {
    return 'preview';
  }
  
  // Default fallback
  return 'unknown';
};

class DebugUtils {
  private static instance: DebugUtils;
  
  static getInstance(): DebugUtils {
    if (!DebugUtils.instance) {
      DebugUtils.instance = new DebugUtils();
    }
    return DebugUtils.instance;
  }

  // Global debug control
  enable() {
    localStorage.setItem('debug_mode', 'true');
    localStorage.setItem('debugLogLevel', LogLevel.DEBUG.toString());
    this.enableAllCategories();
    console.log('🔧 [DebugUtils] Debug mode ENABLED - All logs will be shown');
    console.log('🔧 [DebugUtils] Reload the page to see all debug logs');
    this.logCurrentStatus();
  }

  disable() {
    localStorage.removeItem('debug_mode');
    localStorage.setItem('debugLogLevel', LogLevel.ERROR.toString());
    this.disableAllCategories();
    console.log('🔧 [DebugUtils] Debug mode DISABLED - Only errors will be shown');
    this.logCurrentStatus();
  }

  // Level control
  setLevel(level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR') {
    const levelValue = LogLevel[level];
    if (levelValue !== undefined) {
      localStorage.setItem('debugLogLevel', levelValue.toString());
      console.log(`🔧 [DebugUtils] Log level set to: ${level}`);
      this.logCurrentStatus();
    } else {
      console.error('🔧 [DebugUtils] Invalid level. Use: DEBUG, INFO, WARN, ERROR');
    }
  }

  // Category-specific controls
  enableChat() {
    localStorage.setItem('debug_chat', 'true');
    localStorage.setItem('debugLogLevel', LogLevel.DEBUG.toString());
    console.log('💬 [DebugUtils] Chat debug ENABLED');
    this.logCurrentStatus();
  }

  disableChat() {
    localStorage.removeItem('debug_chat');
    console.log('💬 [DebugUtils] Chat debug DISABLED');
    this.logCurrentStatus();
  }

  enableRealtime() {
    localStorage.setItem('debug_realtime', 'true');
    localStorage.setItem('debugLogLevel', LogLevel.DEBUG.toString());
    console.log('🔄 [DebugUtils] Realtime debug ENABLED');
    this.logCurrentStatus();
  }

  disableRealtime() {
    localStorage.removeItem('debug_realtime');
    console.log('🔄 [DebugUtils] Realtime debug DISABLED');
    this.logCurrentStatus();
  }

  enableNotifications() {
    localStorage.setItem('debug_notifications', 'true');
    localStorage.setItem('debug_global_notifications', 'true');
    localStorage.setItem('debugLogLevel', LogLevel.DEBUG.toString());
    console.log('🔔 [DebugUtils] Notifications debug ENABLED');
    this.logCurrentStatus();
  }

  disableNotifications() {
    localStorage.removeItem('debug_notifications');
    localStorage.removeItem('debug_global_notifications');
    console.log('🔔 [DebugUtils] Notifications debug DISABLED');
    this.logCurrentStatus();
  }

  enableDataCentral() {
    localStorage.setItem('debug_data_central', 'true');
    localStorage.setItem('debugLogLevel', LogLevel.DEBUG.toString());
    console.log('🗄️ [DebugUtils] Data Central debug ENABLED');
    this.logCurrentStatus();
  }

  disableDataCentral() {
    localStorage.removeItem('debug_data_central');
    console.log('🗄️ [DebugUtils] Data Central debug DISABLED');
    this.logCurrentStatus();
  }

  // Status and diagnostics
  status() {
    console.log('📊 [DebugUtils] Current Debug Status:');
    this.logCurrentStatus();
  }

  private logCurrentStatus() {
    const debugMode = localStorage.getItem('debug_mode') === 'true';
    const currentLevel = localStorage.getItem('debugLogLevel') || LogLevel.WARN.toString();
    const levelName = Object.keys(LogLevel)[Object.values(LogLevel).indexOf(Number(currentLevel))] || 'WARN';
    const environment = getEnvironment();
    
    const categories = {
      'Environment': environment,
      'Global Debug': debugMode,
      'Chat': localStorage.getItem('debug_chat') === 'true',
      'Realtime': localStorage.getItem('debug_realtime') === 'true',
      'Notifications': localStorage.getItem('debug_notifications') === 'true',
      'Data Central': localStorage.getItem('debug_data_central') === 'true',
    };

    console.table({
      'Log Level': levelName,
      ...categories
    });
  }

  // CORREÇÃO: Detectar ambiente corretamente usando lógica alinhada com main.tsx
  private isLocalDevelopment(): boolean {
    try {
      const environment = getEnvironment();
      return environment === 'local' || environment === 'development' || 
             localStorage.getItem('debug_mode') === 'true';
    } catch {
      return false;
    }
  }

  // NEW: RealtimeCentral specific diagnostics
  realtimeStatus() {
    console.log('🔄 [DebugUtils] RealtimeCentral Status:');
    
    try {
      const status = realtimeCentral.getDebugStatus();
      
      console.table({
        'Is Activated': status.isActivated,
        'Is Initializing': status.isInitializing,
        'Current User ID': status.currentUserId,
        'Subscribers Count': status.subscribersCount,
        'Chat Channels Count': status.chatChannelsCount,
        'Has Notification Channel': status.hasNotificationChannel,
        'Has Presence Channel': status.hasPresenceChannel,
        'Has Connections Channel': status.hasConnectionsChannel,
        'Has Conversations Channel': status.hasConversationsChannel,
        'Retry Count': status.retryCount,
        'Circuit Breaker Open': status.circuitBreakerOpen,
        'Consecutive Failures': status.consecutiveFailures,
        'Cooldown Remaining (ms)': status.cooldownRemaining
      });

      if (status.chatChannelsList.length > 0) {
        console.log('💬 [DebugUtils] Active Chat Channels:', status.chatChannelsList);
      }

      if (status.subscribersList.length > 0) {
        console.log('👥 [DebugUtils] Active Subscribers:', status.subscribersList);
      }

      // Health assessment
      const isHealthy = status.isActivated && 
                       !status.circuitBreakerOpen && 
                       status.currentUserId &&
                       !status.isInitializing;

      console.log(`🏥 [DebugUtils] System Health: ${isHealthy ? '✅ HEALTHY' : '❌ UNHEALTHY'}`);

      if (!isHealthy) {
        this.suggestRealtimeFixes(status);
      }

    } catch (error) {
      console.error('🔄 [DebugUtils] Error getting realtime status:', error);
    }
  }

  // NEW: Force realtime reconnection
  forceRealtimeReconnect() {
    console.log('🔄 [DebugUtils] Forcing RealtimeCentral reconnection...');
    
    try {
      realtimeCentral.forceReconnect();
      console.log('🔄 [DebugUtils] Reconnection initiated. Check status in a few seconds with DebugUtils.realtimeStatus()');
      
      // Auto-check status after a delay
      setTimeout(() => {
        console.log('🔄 [DebugUtils] Auto-checking status after reconnect:');
        this.realtimeStatus();
      }, 3000);

    } catch (error) {
      console.error('🔄 [DebugUtils] Error forcing reconnect:', error);
    }
  }

  // NEW: Suggest fixes based on realtime status
  private suggestRealtimeFixes(status: any) {
    console.log('\n💡 [DebugUtils] Suggested Fixes:');
    
    if (status.circuitBreakerOpen) {
      console.log('🛑 Circuit breaker is open. Try: DebugUtils.forceRealtimeReconnect()');
    }
    
    if (!status.currentUserId) {
      console.log('👤 No user ID found. Check authentication status.');
    }
    
    if (status.isInitializing) {
      console.log('⏳ System is still initializing. Wait a moment and check again.');
    }
    
    if (status.consecutiveFailures > 0) {
      console.log(`❌ ${status.consecutiveFailures} consecutive failures detected. This may indicate connection issues.`);
    }
    
    if (status.cooldownRemaining > 0) {
      console.log(`⏱️ Cooldown active for ${Math.round(status.cooldownRemaining / 1000)}s. System will retry automatically.`);
    }
    
    console.log('\n🔧 [DebugUtils] General troubleshooting:');
    console.log('  1. Check network connection');
    console.log('  2. Verify Supabase project settings');
    console.log('  3. Check browser console for additional errors');
    console.log('  4. Try refreshing the page');
}

  // Chat-specific diagnostics
  chatStatus() {
    console.log('💬 [DebugUtils] Chat System Status:');
    
    try {
      const isActive = realtimeCentral.isActive();
      const currentPath = window.location.pathname;
      const isInChat = currentPath.includes('/chat/');
      
      console.table({
        'RealtimeCentral Active': isActive,
        'Current Path': currentPath,
        'In Chat': isInChat,
        'User Agent': navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'
      });

      if (isInChat) {
        console.log('💬 [DebugUtils] Chat partner detected');
      }

      // Also show realtime status for chat context
      this.realtimeStatus();

    } catch (error) {
      console.error('💬 [DebugUtils] Error checking chat status:', error);
    }
  }

  testRealtime() {
    console.log('🔄 [DebugUtils] Testing Realtime Connection...');
    this.realtimeStatus();
  }

  showSubscribers() {
    console.log('👥 [DebugUtils] Showing Realtime Subscribers...');
    
    try {
      const status = realtimeCentral.getDebugStatus();
      
      if (status.subscribersList.length === 0) {
        console.log('👥 [DebugUtils] No active subscribers');
      } else {
        console.log('👥 [DebugUtils] Active subscribers:', status.subscribersList);
        console.log(`👥 [DebugUtils] Total: ${status.subscribersCount} subscribers`);
      }
      
    } catch (error) {
      console.error('👥 [DebugUtils] Error showing subscribers:', error);
    }
  }

  // Utility functions
  clear() {
    console.clear();
    console.log('🧹 [DebugUtils] Console cleared');
  }

  reload() {
    console.log('🔄 [DebugUtils] Reloading page...');
    window.location.reload();
  }

  help() {
    console.log(`
🔧 DebugUtils Help - Available Commands:

📋 GENERAL CONTROL:
  DebugUtils.enable()          - Enable all debug logs
  DebugUtils.disable()         - Disable debug (errors only)
  DebugUtils.setLevel(level)   - Set log level (DEBUG/INFO/WARN/ERROR)
  DebugUtils.status()          - Show current debug status

🎯 CATEGORY CONTROL:
  DebugUtils.enableChat()      - Debug chat system only
  DebugUtils.enableRealtime()  - Debug realtime only  
  DebugUtils.enableNotifications() - Debug notifications only
  DebugUtils.enableDataCentral()   - Debug data central only
  
  (Use disable* versions to turn off specific categories)

🔄 REALTIME DIAGNOSTICS:
  DebugUtils.realtimeStatus()        - Show detailed realtime status
  DebugUtils.forceRealtimeReconnect() - Force reconnection
  DebugUtils.testRealtime()          - Test realtime connection
  DebugUtils.showSubscribers()       - Show active subscribers

💬 CHAT DIAGNOSTICS:
  DebugUtils.chatStatus()      - Show chat system status

🛠️ UTILITIES:
  DebugUtils.clear()           - Clear console
  DebugUtils.reload()          - Reload page
  DebugUtils.help()            - Show this help

💡 TIP: After enabling debug, reload the page to see all logs!
🚨 NEW: Auto-enabled on Lovable domains (lovable.dev, lovableproject.com, lovable.app)
    `);
  }

  // Private helpers
  private enableAllCategories() {
    localStorage.setItem('debug_notifications', 'true');
    localStorage.setItem('debug_global_notifications', 'true');
    localStorage.setItem('debug_data_central', 'true');
    localStorage.setItem('debug_chat', 'true');
    localStorage.setItem('debug_realtime', 'true');
  }

  private disableAllCategories() {
    localStorage.removeItem('debug_notifications');
    localStorage.removeItem('debug_global_notifications');
    localStorage.removeItem('debug_data_central');
    localStorage.removeItem('debug_chat');
    localStorage.removeItem('debug_realtime');
  }
}

// Export instance
export const debugUtils = DebugUtils.getInstance();

// Make available globally for easy console access
if (typeof window !== 'undefined') {
  (window as any).DebugUtils = debugUtils;
  
  // Show helpful message on load
  const environment = getEnvironment();
  if (environment === 'local' || environment === 'development') {
    debugLogger.debug('init', '🔧 DebugUtils loaded! Type "DebugUtils.help()" for available commands');
    debugLogger.debug('init', '🔧 Quick start: "DebugUtils.enable()" to see all logs');
    debugLogger.debug('init', `🚨 Environment detected: ${environment}`);
  }
}

// Legacy support for existing functions
export const enableDebugMode = () => debugUtils.enable();
export const disableDebugMode = () => debugUtils.disable();
export const checkDebugStatus = () => debugUtils.status();
export const enableNotificationDebug = () => debugUtils.enableNotifications();
export const disableNotificationDebug = () => debugUtils.disableNotifications();

// Conditional logging functions - now using centralized debugLogger
export const debugLog = (category: string, message: string, data?: any) => {
  const debugEnabled = localStorage.getItem('debug_mode') === 'true' || 
                      localStorage.getItem(`debug_${category}`) === 'true';
  
  if (debugEnabled) {
    debugLogger.debug(category, message, data);
  }
};

export const debugWarn = (category: string, message: string, data?: any) => {
  debugLogger.warn(category, message, data);
};

export const debugError = (category: string, message: string, error?: any) => {
  debugLogger.error(category, message, error);
};
